import React, { useEffect, useRef, useLayoutEffect } from 'react';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useLoading } from '../context/LoadingContext';

gsap.registerPlugin(ScrollTrigger);

const HorizontalScrollSection = ({ children, className = '' }) => {
  const sectionRef = useRef(null);
  const containerRef = useRef(null);
  const { isInitialized } = useLoading();
  const tlRef = useRef(null);

  // Use useLayoutEffect to prevent flash of unstyled content
  useLayoutEffect(() => {
    if (!isInitialized || !sectionRef.current || !containerRef.current) return;

    const section = sectionRef.current;
    const container = containerRef.current;

    // Ensure the container has content before proceeding
    if (!container.children.length) {
      console.warn('HorizontalScrollSection: No children found in container');
      return;
    }

    try {
      // Reset any existing ScrollTrigger instances
      ScrollTrigger.getAll().forEach(t => t.kill());

      // Set initial styles with more explicit sizing
      gsap.set(section, {
        height: window.innerHeight, // Force section height
        overflow: 'hidden',
        position: 'relative'
      });

      gsap.set(container, {
        display: 'flex',
        width: 'max-content',
        height: '100%',
        position: 'absolute',
        left: 0,
        top: 0
      });

      // Calculate the scrolling distance more accurately
      const distance = container.scrollWidth - window.innerWidth;
      
      if (distance <= 0) {
        console.warn('HorizontalScrollSection: Content width is not greater than viewport width');
        return;
      }

      // Create the horizontal scrolling effect with improved configuration
      tlRef.current = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: `+=${distance}`,
          pin: true,
          anticipatePin: 1,
          scrub: 0.5,
          invalidateOnRefresh: true,
          onUpdate: self => {
            // Optional: Add progress tracking for debugging
            // console.log('Scroll progress:', self.progress.toFixed(3));
          },
          onRefresh: () => {
            // Recalculate distances on refresh
            const newDistance = container.scrollWidth - window.innerWidth;
            if (tlRef.current) {
              tlRef.current.clear();
              tlRef.current.to(container, {
                x: -newDistance,
                ease: 'none',
                duration: 1
              });
            }
          }
        }
      });

      tlRef.current.to(container, {
        x: -distance,
        ease: 'none',
        duration: 1
      });

      // Enhanced fade effects for children with better timing
      const children = container.children;
      Array.from(children).forEach((child, i) => {
        gsap.from(child, {
          opacity: 0,
          scale: 0.95,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: child,
            containerAnimation: tlRef.current,
            start: 'left center',
            end: 'right center',
            toggleActions: 'play none none reverse',
            onEnter: () => console.log(`Section ${i + 1} entered`),
            onLeave: () => console.log(`Section ${i + 1} left`)
          }
        });
      });

    } catch (error) {
      console.error('Error in HorizontalScrollSection setup:', error);
    }

    // Cleanup function
    return () => {
      if (tlRef.current) {
        tlRef.current.kill();
      }
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, [isInitialized]);

  return (
    <section 
      ref={sectionRef} 
      className={`fixed-section w-full ${className}`}
      style={{ 
        minHeight: '100vh',
        visibility: isInitialized ? 'visible' : 'hidden' 
      }}
    >
      <div 
        ref={containerRef} 
        className="relative h-full flex items-center"
      >
        {children}
      </div>
    </section>
  );
};

export default HorizontalScrollSection; 